import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useQuery } from "@tanstack/react-query";
import getSiteChartData from "../http/getSiteChartData";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

/**
 * Fetch a chart data payload from the standard /site/<id>/chart_data/<type>
 * endpoint. Generic; the caller picks the dataType + options shape and casts
 * the result.
 */
export const useSiteChartData = <T = unknown>(
  siteId: string,
  dataType: string,
  options: Record<string, unknown> = {},
  // Optional react-query enabled gate so consumers can defer fetching
  // until they have all the inputs they need.
  enabled = true
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["siteChartData", siteId, dataType, options],
    async () =>
      (await getSiteChartData(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        dataType,
        options
      )) as T,
    { enabled: !!siteId && enabled }
  );
};

/**
 * Batched variant: fetches the same chart-data shape for every site in
 * `siteIds` in parallel. Returns `{ [siteId]: T | undefined }`. Single
 * useQuery so React's rules-of-hooks aren't violated by callers whose
 * site-id list changes shape between renders (e.g. the portfolio
 * taxonomy review page where DnD moves sites between cluster cards).
 */
export const useSiteChartDataMany = <T = unknown>(
  siteIds: string[],
  dataType: string,
  options: Record<string, unknown> = {}
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  // Stable key: sorted ids prevent permutations from triggering refetch.
  const sortedIds = [...siteIds].sort();
  return useQuery(
    ["siteChartDataMany", dataType, sortedIds.join("|"), options],
    async () => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      const entries = await Promise.all(
        sortedIds.map(async (sid) => {
          try {
            const data = (await getSiteChartData(
              token,
              sid,
              dataType,
              options
            )) as T;
            return [sid, data] as const;
          } catch {
            return [sid, undefined] as const;
          }
        })
      );
      const out: Record<string, T | undefined> = {};
      entries.forEach(([sid, data]) => {
        out[sid] = data;
      });
      return out;
    },
    { enabled: sortedIds.length > 0 }
  );
};
